import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Save, Upload, X, Plus, User, Target, Award, Link2, FileText, ExternalLink, FileUp } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, Spinner, UserAvatar } from "@/components/ui-bits";
import { SOCIAL_FIELDS } from "@/components/Socials";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FIELDS, LANGUAGES, COUNTRIES } from "@/lib/constants";

function ChipToggle({ options, value, onChange, testid }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)}
            className={`text-sm font-medium rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function TagInput({ value, onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const add = () => { const t = text.trim(); if (t && !value.includes(t)) onChange([...value, t]); setText(""); };
  return (
    <div>
      <div className="flex gap-2">
        <Input data-testid={testid} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="h-11 rounded-lg" />
        <Button type="button" variant="outline" onClick={add} className="rounded-lg shrink-0 h-11"><Plus size={16} /></Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2.5">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground rounded-full pl-3 pr-1.5 py-1 text-sm">
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="text-muted-foreground hover:text-destructive"><X size={13} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ title, icon: Icon, description, children }) {
  return (
    <div className="card-milled rounded-[0.8rem] p-6 sm:p-7">
      <div className="flex items-center gap-3 mb-5">
        {Icon && (
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary/80 text-primary shrink-0">
            <Icon size={17} strokeWidth={1.75} />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function ExpertProfileEditor() {
  const { t } = useTranslation();
  const fileRef = useRef();
  const cvRef = useRef();
  const [form, setForm] = useState(null);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [country, setCountry] = useState("Remote");
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);

  useEffect(() => {
    api.get("/expert/me").then(({ data }) => {
      if (!data) return;
      const socials = {};
      SOCIAL_FIELDS.forEach((s) => { socials[s.key] = data.socials?.[s.key] || ""; });
      setForm({
        name: data.name || "", title: data.title || "", headline: data.headline || "", bio: data.bio || "",
        fields: data.fields || [], expertise: data.expertise || [], languages: data.languages || ["English"],
        timezone: data.timezone || "", years_experience: data.years_experience || 0,
        certifications: data.certifications || [], achievements: data.achievements || [],
        picture: data.picture || null, socials,
        cv_url: data.cv_url || null, cv_name: data.cv_name || "",
      });
      setSessionTypes(data.session_types || []);
      const loc = data.location || "";
      if (loc.includes(", ")) { const [c, co] = loc.split(", "); setCity(c); setCountry(co); }
      else if (loc) setCountry(loc);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("picture", data.url);
      toast.success(t("expert.profile.toastPhoto"));
    } catch (err) { toast.error(apiError(err, t("expert.profile.toastUploadFailed"))); } finally { setUploading(false); }
  };

  const uploadCv = async (file) => {
    if (!file) return;
    setUploadingCv(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cv_url: data.url, cv_name: data.filename }));
      toast.success(t("expert.profile.toastCv"));
    } catch (err) { toast.error(apiError(err, t("expert.profile.toastUploadFailed"))); } finally { setUploadingCv(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const location = city ? `${city}, ${country}` : country;
      await api.post("/expert/profile", { ...form, location, session_types: sessionTypes });
      toast.success(t("expert.profile.toastSaved"));
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  if (!form) return <AppShell><div className="grid place-items-center py-32"><Spinner className="text-primary w-7 h-7" /></div></AppShell>;

  return (
    <AppShell>
      <PageHeader eyebrow={t("expert.eyebrow")} title={t("expert.profile.title")} description={t("expert.profile.subtitle")}>
        <Button data-testid="save-profile" onClick={save} disabled={saving} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
          {saving ? <Spinner /> : <><Save size={16} /> {t("expert.profile.save")}</>}
        </Button>
      </PageHeader>

      <div className="space-y-6 max-w-3xl animate-fade-up">
        {/* Basics */}
        <Card title={t("expert.profile.basics")} icon={User} description={t("expert.profile.basicsDesc")}>
          <div className="flex items-center gap-4 mb-5">
            <UserAvatar src={fileUrl(form.picture)} name={form.name} size={72} />
            <div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} data-testid="photo-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-lg gap-1.5">
                {uploading ? <Spinner /> : <><Upload size={15} /> {t("expert.profile.uploadPhoto")}</>}
              </Button>
              <p className="text-xs text-muted-foreground mt-1.5">{t("expert.profile.photoHint")}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>{t("expert.profile.fullName")}</Label><Input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-lg" /></div>
            <div className="space-y-1.5"><Label>{t("expert.profile.proTitle")}</Label><Input data-testid="pf-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("expert.profile.proTitlePlaceholder")} className="h-11 rounded-lg" /></div>
          </div>
          <div className="space-y-1.5 mt-4"><Label>{t("expert.profile.headline")}</Label><Input data-testid="pf-headline" value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder={t("expert.profile.headlinePlaceholder")} className="h-11 rounded-lg" /></div>
          <div className="space-y-1.5 mt-4"><Label>{t("expert.profile.bio")}</Label><Textarea data-testid="pf-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder={t("expert.profile.bioPlaceholder")} className="rounded-lg min-h-32" /></div>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div className="space-y-1.5"><Label>{t("expert.profile.country")}</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="pf-country" className="h-11 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{(country && !COUNTRIES.includes(country) ? [country, ...COUNTRIES] : COUNTRIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>{t("expert.profile.city")}</Label><Input data-testid="pf-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("expert.profile.cityPlaceholder")} className="h-11 rounded-lg" /></div>
            <div className="space-y-1.5"><Label>{t("expert.profile.years")}</Label><Input data-testid="pf-years" type="number" min="0" value={form.years_experience} onChange={(e) => set("years_experience", Number(e.target.value))} className="h-11 rounded-lg tabular" /></div>
          </div>
        </Card>

        {/* Expertise */}
        <Card title={t("expert.profile.focusAreas")} icon={Target} description={t("expert.profile.focusAreasDesc")}>
          <Label className="mb-2 block">{t("expert.profile.fieldsLabel")}</Label>
          <ChipToggle options={FIELDS} value={form.fields} onChange={(v) => set("fields", v)} testid="pf-fields" />
          <div className="mt-5 space-y-1.5">
            <Label>{t("expert.profile.expertiseLabel")}</Label>
            <TagInput value={form.expertise} onChange={(v) => set("expertise", v)} placeholder={t("expert.profile.expertisePlaceholder")} testid="pf-expertise" />
          </div>
          <div className="mt-5">
            <Label className="mb-2 block">{t("expert.profile.languages")}</Label>
            <ChipToggle options={LANGUAGES} value={form.languages} onChange={(v) => set("languages", v)} testid="pf-languages" />
          </div>
        </Card>

        {/* Credentials */}
        <Card title={t("expert.profile.credentials")} icon={Award} description={t("expert.profile.credentialsDesc")}>
          <div className="space-y-1.5"><Label>{t("expert.profile.certifications")}</Label>
            <TagInput value={form.certifications} onChange={(v) => set("certifications", v)} placeholder={t("expert.profile.certificationsPlaceholder")} testid="pf-certs" />
          </div>
          <div className="mt-5 space-y-1.5"><Label>{t("expert.profile.achievements")}</Label>
            <TagInput value={form.achievements} onChange={(v) => set("achievements", v)} placeholder={t("expert.profile.achievementsPlaceholder")} testid="pf-ach" />
          </div>
        </Card>

        {/* CV / Resume */}
        <Card title={t("expert.profile.cv")} icon={FileText} description={t("expert.profile.cvDesc")}>
          <input ref={cvRef} type="file" accept=".pdf,.doc,.docx,application/pdf" hidden onChange={(e) => uploadCv(e.target.files[0])} data-testid="cv-input" />
          {form.cv_url ? (
            <div className="flex items-center gap-3 rounded-lg border border-border p-3.5">
              <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/10 text-primary shrink-0"><FileText size={18} /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{form.cv_name || t("expert.profile.cvDefault")}</p>
                <a href={fileUrl(form.cv_url)} target="_blank" rel="noreferrer" data-testid="cv-view" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5">{t("expert.profile.cvView")} <ExternalLink size={12} /></a>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button type="button" variant="outline" size="sm" className="rounded-lg" onClick={() => cvRef.current?.click()} disabled={uploadingCv}>{uploadingCv ? <Spinner /> : t("expert.profile.cvReplace")}</Button>
                <Button type="button" variant="ghost" size="sm" className="rounded-lg text-muted-foreground hover:text-destructive" onClick={() => setForm((f) => ({ ...f, cv_url: null, cv_name: "" }))} data-testid="cv-remove"><X size={15} /></Button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => cvRef.current?.click()} disabled={uploadingCv} data-testid="cv-upload"
              className="w-full rounded-lg border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 transition-colors p-6 flex flex-col items-center gap-2 text-center">
              {uploadingCv ? <Spinner className="text-primary" /> : <FileUp size={22} className="text-muted-foreground" strokeWidth={1.5} />}
              <span className="text-sm font-medium">{t("expert.profile.cvUpload")}</span>
              <span className="text-xs text-muted-foreground">{t("expert.profile.cvUploadHint")}</span>
            </button>
          )}
        </Card>

        {/* Links & socials */}
        <Card title={t("expert.profile.links")} icon={Link2} description={t("expert.profile.linksDesc")}>
          <div className="grid sm:grid-cols-2 gap-4">
            {SOCIAL_FIELDS.map((s) => (
              <div key={s.key} className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><s.icon size={14} /> {s.label}</Label>
                <Input data-testid={`pf-${s.key}`} value={form.socials[s.key] || ""} onChange={(e) => set("socials", { ...form.socials, [s.key]: e.target.value })} placeholder={s.placeholder} className="h-11 rounded-lg" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
